<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from '@vueform/multiselect';
import { api } from '@/helpers/common/api';
import AtendimentoVisaoToggle from './AtendimentoVisaoToggle.vue';
import IniciarConversaModal from './IniciarConversaModal.vue';

// Nome em "Título" (primeira letra de cada palavra maiúscula, resto minúsculo).
// Contato.nome é salvo em UPPERCASE no banco — isto normaliza só para exibição.
function tituloCaso(texto) {
  return String(texto || '')
    .toLowerCase()
    .replace(/(^|\s)(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());
}

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT',
  'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];
const STATUS_OPCOES = [
  { value: '', label: 'Todos' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'inativo', label: 'Inativo' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

export default {
  name: 'InboxList',
  components: { AtendimentoVisaoToggle, IniciarConversaModal, Multiselect },
  props: {
    visao: { type: String, default: 'lista' },
  },
  emits: ['change-visao'],
  data() {
    return {
      buscaLocal: '',
      buscaAberta: false,
      filtroTab: 'todas', // 'todas' | 'nao_lidas' | 'atribuidas'
      menuAberto: false,
      modalNovaConversa: false,
      // Menu de ações por card (3-dot que aparece no hover).
      menuConvAbertoId: null,
      // Modal de finalizar conversa.
      finalizarModalAberto: false,
      finalizarConversaObj: null,
      finalizarJanelaInfo: null,
      mensagemFinalTexto: '',
      finalizandoConversa: false,
      // Painel de filtros avançados (etiquetas/UF/documento/status).
      filtrosAbertos: false,
      etiquetasDisponiveis: [],
      ufs: UFS,
      statusOpcoes: STATUS_OPCOES,
    };
  },
  computed: {
    ...mapGetters('atendimento', ['conversasFiltradas', 'loading', 'filtros']),
    // Bind dos filtros do store — mesmo backing do KanbanFiltros, então
    // alterar aqui afeta a lista no kanban também (intencional).
    filtroBusca: {
      get() { return this.filtros.busca || ''; },
      set(v) { this.setFiltro({ campo: 'busca', valor: v || '' }); },
    },
    filtroStatus: {
      get() { return this.filtros.status || ''; },
      set(v) { this.setFiltro({ campo: 'status', valor: v || '' }); },
    },
    filtroEstados: {
      get() { return this.filtros.estados || []; },
      set(v) { this.setFiltro({ campo: 'estados', valor: v || [] }); },
    },
    filtroRotulosIds: {
      get() { return this.filtros.rotulosIds || []; },
      set(v) { this.setFiltro({ campo: 'rotulosIds', valor: v || [] }); },
    },
    temFiltrosAtivos() {
      const f = this.filtros || {};
      return Boolean(f.busca || f.status
        || (f.estados || []).length
        || (f.rotulosIds || []).length);
    },
    conversaAtivaId() {
      return this.$store.state.atendimento.conversaAtivaId;
    },
    currentUserId() {
      return this.$store.getters['auth/currentUser']?.id;
    },
    currentUserNome() {
      const u = this.$store.getters['auth/currentUser'];
      return u?.nome || u?.name || 'Usuário';
    },
    conversasOrdenadas() {
      // Conversas finalizadas saem da lista — elas continuam acessíveis
      // pelo kanban na coluna "Encerrado".
      let lista = this.conversasFiltradas.filter((c) => {
        const tipo = c.estagio && c.estagio.tipo;
        return tipo !== 'finalizado';
      });

      if (this.filtroTab === 'nao_lidas') {
        lista = lista.filter((c) => c.nao_lidas_count > 0);
      } else if (this.filtroTab === 'atribuidas') {
        lista = lista.filter((c) => c.atendente_id === this.currentUserId);
      }

      if (this.buscaLocal.trim()) {
        const termo = this.buscaLocal.toLowerCase().trim();
        lista = lista.filter((c) => {
          const contato = c.contato || c.Contato || {};
          const campos = [
            contato.nome, contato.sobrenome, contato.telefone,
          ].filter(Boolean).map((v) => String(v).toLowerCase());
          return campos.some((v) => v.includes(termo));
        });
      }

      return lista.sort((a, b) => {
        // Fixadas no topo (futuro)
        const ta = new Date(a.ultima_mensagem_em || a.updated_at || 0).getTime();
        const tb = new Date(b.ultima_mensagem_em || b.updated_at || 0).getTime();
        return tb - ta;
      });
    },
    totalNaoLidas() {
      return this.conversasFiltradas.reduce((sum, c) => sum + (c.nao_lidas_count || 0), 0);
    },
    contNaoLidas() {
      return this.conversasFiltradas.filter((c) => c.nao_lidas_count > 0).length;
    },
  },
  mounted() {
    document.addEventListener('click', this._fecharMenu);
    this._carregarEtiquetas();
  },
  beforeUnmount() {
    document.removeEventListener('click', this._fecharMenu);
  },
  methods: {
    ...mapActions('atendimento', ['selecionarConversa', 'fetchConversas', 'finalizarConversa', 'verificarJanela', 'setFiltro', 'resetFiltros']),

    // ── Filtros avançados ─────────────────────────────────────────────
    toggleFiltros() {
      this.filtrosAbertos = !this.filtrosAbertos;
    },
    limparFiltros() {
      this.resetFiltros();
    },
    toggleEstado(uf) {
      const set = new Set(this.filtroEstados);
      if (set.has(uf)) set.delete(uf);
      else set.add(uf);
      this.filtroEstados = [...set];
    },
    async _carregarEtiquetas() {
      try {
        const r = await api.read('/rotulo?limit=9999');
        const lista = Array.isArray(r) ? r : (r && r.data) || [];
        // Formato esperado pelo @vueform/multiselect (label/value).
        this.etiquetasDisponiveis = lista.map((x) => ({
          value: x.id,
          label: x.nome,
          cor: x.cor_referencia || '#6c757d',
        }));
      } catch (_e) { this.etiquetasDisponiveis = []; }
    },

    selecionar(id) {
      this.selecionarConversa(id);
    },
    abrirBusca() {
      this.buscaAberta = true;
      this.$nextTick(() => this.$refs.searchInput?.focus());
    },
    fecharBusca() {
      this.buscaAberta = false;
      this.buscaLocal = '';
    },
    toggleMenu() {
      this.menuAberto = !this.menuAberto;
    },
    _fecharMenu(e) {
      if (this.menuAberto && this.$refs.menuDrop && !this.$refs.menuDrop.contains(e.target)) {
        this.menuAberto = false;
      }
      // Click fora do menu de card → fecha. O menu vive dentro de cada
      // .wa-item; checa se o alvo está dentro de um .wa-item-menu-wrap.
      if (this.menuConvAbertoId !== null && !e.target.closest('.wa-item-menu-wrap')) {
        this.menuConvAbertoId = null;
      }
      // Click fora do painel de filtros avançados → fecha.
      if (this.filtrosAbertos && !e.target.closest('.wa-filter-popover-wrap')) {
        this.filtrosAbertos = false;
      }
    },
    toggleCardMenu(convId) {
      this.menuConvAbertoId = this.menuConvAbertoId === convId ? null : convId;
    },
    async abrirFinalizar(conv) {
      this.menuConvAbertoId = null;
      this.finalizarConversaObj = conv;
      this.mensagemFinalTexto = '';
      this.finalizarJanelaInfo = null;
      this.finalizarModalAberto = true;
      // Busca o estado da janela 24h pra esse contato — se fechada,
      // bloqueamos a textarea (mesma lógica do ChatDrawer).
      const contatoId = conv && (conv.contato_id || (conv.contato && conv.contato.id));
      if (contatoId) {
        try {
          this.finalizarJanelaInfo = await this.verificarJanela(contatoId);
        } catch (_e) {
          this.finalizarJanelaInfo = { aberta: false, motivo: 'erro-consulta' };
        }
      }
    },
    cancelarFinalizar() {
      if (this.finalizandoConversa) return;
      this.finalizarModalAberto = false;
      this.finalizarConversaObj = null;
      this.finalizarJanelaInfo = null;
      this.mensagemFinalTexto = '';
    },
    async confirmarFinalizar() {
      if (!this.finalizarConversaObj || this.finalizandoConversa) return;
      const texto = String(this.mensagemFinalTexto || '').trim();
      const janelaFechada = this.finalizarJanelaInfo
        ? !this.finalizarJanelaInfo.aberta
        : false;
      if (texto && janelaFechada) {
        const msg = 'Janela 24h fechada — não é possível enviar texto livre. Deixe a mensagem vazia ou envie um template antes.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
        return;
      }
      this.finalizandoConversa = true;
      try {
        const idFinalizada = this.finalizarConversaObj.id;
        await this.finalizarConversa({
          conversaId: idFinalizada,
          mensagemFinal: texto || null,
        });
        this.finalizarModalAberto = false;
        this.finalizarConversaObj = null;
        this.finalizarJanelaInfo = null;
        this.mensagemFinalTexto = '';
        // Se a conversa finalizada estava aberta no drawer, recolhe o
        // drawer pra forma inerte (mesmo comportamento de finalizar pelo
        // próprio drawer).
        if (this.conversaAtivaId === idFinalizada) {
          this.selecionarConversa(null);
        }
      } catch (error) {
        const msg = (error && error.response && error.response.data && error.response.data.error) || 'Falha ao finalizar conversa.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
      } finally {
        this.finalizandoConversa = false;
      }
    },

    // --- Contato helpers ---
    contato(c) {
      return c.contato || c.Contato || {};
    },
    nomeContato(c) {
      const ct = this.contato(c);
      if (!ct || (!ct.nome && !ct.id)) return `Contato #${c.contato_id}`;
      const nome = String(ct.nome || '').trim();
      const sobrenome = String(ct.sobrenome || '').trim();
      if (nome && sobrenome) return `${nome} ${sobrenome}`;
      if (nome) return nome;
      return `Contato #${ct.id || c.contato_id}`;
    },
    iniciais(c) {
      const nome = this.nomeContato(c);
      const partes = nome.replace(/^Contato #\d+$/, '?').split(/\s+/).filter(Boolean);
      if (partes.length >= 2) return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
      return (partes[0] || '?')[0].toUpperCase();
    },
    corAvatar(c) {
      const nome = this.nomeContato(c);
      const cores = [
        '#00a884', '#53bdeb', '#7f66ff', '#ff6b6b',
        '#ffa348', '#02b886', '#6c8ea4', '#d066e2',
        '#e4a51b', '#5eb5f7', '#e06c75', '#61afef',
      ];
      let hash = 0;
      for (let i = 0; i < nome.length; i++) hash = nome.charCodeAt(i) + ((hash << 5) - hash);
      return cores[Math.abs(hash) % cores.length];
    },

    telefoneContato(c) {
      const ct = this.contato(c);
      const canais = Array.isArray(ct?.canais) ? ct.canais : [];
      const tel = canais.find((x) => x.tipo_canal === 'telefone' && x.principal)
        || canais.find((x) => x.tipo_canal === 'telefone');
      const valor = tel?.valor_canal || tel?.normalizado || ct?.telefone || '';
      if (!valor) return '';
      const digitos = String(valor).replace(/\D/g, '');
      const m = digitos.match(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/);
      if (m) return `(${m[2]}) ${m[3]}-${m[4]}`;
      const m2 = digitos.match(/^(\d{2})(\d{4,5})(\d{4})$/);
      if (m2) return `(${m2[1]}) ${m2[2]}-${m2[3]}`;
      return valor;
    },
    temNome(c) {
      const ct = this.contato(c);
      return Boolean(ct && String(ct.nome || '').trim());
    },
    // Username do WhatsApp/Meta (política de privacidade — às vezes substitui o
    // número). Fica em canal_contato.whatsapp_username.
    metaUsername(c) {
      const ct = this.contato(c);
      const canais = Array.isArray(ct?.canais) ? ct.canais : [];
      const comUser = canais.find((x) => x.whatsapp_username);
      return comUser ? String(comUser.whatsapp_username).trim() : '';
    },
    // Título da conversa: nome cadastrado → username Meta → telefone → #id.
    tituloConversa(c) {
      const ct = this.contato(c);
      const nome = String(ct?.nome || '').trim();
      const sobrenome = String(ct?.sobrenome || '').trim();
      if (nome) return tituloCaso(sobrenome ? `${nome} ${sobrenome}` : nome);
      const user = this.metaUsername(c);
      if (user) return user;
      const tel = this.telefoneContato(c);
      if (tel) return tel;
      return `Contato #${ct?.id || c.contato_id}`;
    },

    // --- Mensagem helpers ---
    previaMsg(c) {
      const m = c.ultima_mensagem;
      if (!m) return '';
      let texto = m.conteudo || '';
      if (!texto) {
        const mapa = {
          imagem: '\u{1F4F7} Imagem',
          audio: '\u{1F3A4} \u00C1udio',
          video: '\u{1F3AC} V\u00EDdeo',
          documento: '\u{1F4C4} Documento',
          template: '\u{1F4CB} Template',
          sistema: '\u2699\uFE0F Sistema',
        };
        texto = mapa[m.tipo] || `[${m.tipo}]`;
      }
      texto = texto.replace(/\s+/g, ' ').trim();
      if (texto.length > 70) texto = `${texto.slice(0, 70)}\u2026`;
      return texto;
    },
    direcaoMsg(c) {
      return c.ultima_mensagem?.direcao || null;
    },
    statusEnvio(c) {
      return c.ultima_mensagem?.status_envio || null;
    },
    estagio(c) {
      return c.estagio || c.ConversaEstagio || null;
    },

    // --- Etiquetas, consentimento, atendente ---
    etiquetas(c) {
      const ct = this.contato(c);
      const lista = ct.rotulos || ct.Rotulo || [];
      return lista.map((r) => ({
        id: r.id,
        nome: r.nome,
        cor: r.cor_referencia || '#6c757d',
      }));
    },
    statusConsentimento(c) {
      return c.status_consentimento || 'pendente';
    },
    badgeConsentimento(c) {
      const map = {
        opt_in:   { texto: 'Opt-in',   classe: 'wa-consent--optin',   icone: 'ri-check-line' },
        opt_out:  { texto: 'Opt-out',  classe: 'wa-consent--optout',  icone: 'ri-forbid-line' },
        pendente: { texto: 'Pendente', classe: 'wa-consent--pending', icone: 'ri-time-line' },
        expirado: { texto: 'Expirado', classe: 'wa-consent--expired', icone: 'ri-history-line' },
      };
      return map[this.statusConsentimento(c)] || map.pendente;
    },
    atendenteNome(c) {
      const a = c.atendente || c.Usuario || null;
      if (!a) return null;
      const nome = a.nome || a.name || `#${a.id}`;
      return nome.split(' ')[0]; // Primeiro nome
    },

    // --- Horário WhatsApp style ---
    horarioWhatsapp(c) {
      const data = c.ultima_mensagem_em || c.updated_at;
      if (!data) return '';
      try {
        const d = new Date(data);
        const agora = new Date();
        const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
        const ontem = new Date(hoje.getTime() - 86400000);
        const dData = new Date(d.getFullYear(), d.getMonth(), d.getDate());

        if (dData.getTime() === hoje.getTime()) {
          return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        }
        if (dData.getTime() === ontem.getTime()) {
          return 'Ontem';
        }
        const diffDias = Math.floor((hoje.getTime() - dData.getTime()) / 86400000);
        if (diffDias < 7) {
          const dias = ['Domingo', 'Segunda', 'Ter\u00E7a', 'Quarta', 'Quinta', 'Sexta', 'S\u00E1bado'];
          return dias[d.getDay()];
        }
        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
      } catch (_e) { return ''; }
    },
  },
};
</script>

<template>
  <div class="wa-sidebar">
    <!-- ===== Header (estilo WhatsApp Web) ===== -->
    <header class="wa-header">
      <div class="wa-header-left">
        <AtendimentoVisaoToggle :visao="visao" @change="$emit('change-visao', $event)" />
      </div>
      <div class="wa-header-actions">
        <button
          type="button"
          class="wa-header-btn"
          title="Nova conversa"
          @click="modalNovaConversa = true"
        >
          <i class="ri-chat-new-line"></i>
        </button>
        <button
          type="button"
          class="wa-header-btn"
          title="Atualizar"
          @click="fetchConversas"
        >
          <i class="ri-refresh-line"></i>
        </button>
        <div ref="menuDrop" class="wa-menu-wrapper">
          <button
            type="button"
            class="wa-header-btn"
            title="Menu"
            @click.stop="toggleMenu"
          >
            <i class="ri-more-2-fill"></i>
          </button>
          <div v-if="menuAberto" class="wa-dropdown">
            <button class="wa-dropdown-item" @click="menuAberto = false; modalNovaConversa = true">
              <i class="ri-chat-new-line me-2"></i>Nova conversa
            </button>
            <button class="wa-dropdown-item" @click="menuAberto = false; fetchConversas()">
              <i class="ri-refresh-line me-2"></i>Atualizar lista
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- ===== Search bar ===== -->
    <div class="wa-search-area">
      <div class="wa-search-box" :class="{ focused: buscaAberta }">
        <button
          v-if="buscaAberta"
          class="wa-search-back"
          @click="fecharBusca"
        >
          <i class="ri-arrow-left-line"></i>
        </button>
        <i v-else class="ri-search-line wa-search-icon" @click="abrirBusca"></i>
        <input
          ref="searchInput"
          v-model="buscaLocal"
          type="text"
          class="wa-search-input"
          placeholder="Pesquisar"
          @focus="buscaAberta = true"
        />
      </div>
      <div class="wa-filter-popover-wrap" @click.stop>
        <button
          class="wa-filter-btn"
          :class="{ 'wa-filter-btn--ativo': temFiltrosAtivos }"
          title="Filtros avançados"
          @click="toggleFiltros"
        >
          <i class="ri-filter-3-line"></i>
          <span v-if="temFiltrosAtivos" class="wa-filter-dot"></span>
        </button>

        <div v-if="filtrosAbertos" class="wa-filter-popover">
          <header class="wa-filter-popover-header">
            <span>Filtros avançados</span>
            <button
              v-if="temFiltrosAtivos"
              type="button"
              class="wa-filter-clear"
              @click="limparFiltros"
            >Limpar</button>
          </header>

          <!-- Busca textual (nome / telefone / documento) -->
          <div class="wa-filter-section">
            <label class="wa-filter-label">Nome, telefone ou documento</label>
            <input
              v-model="filtroBusca"
              type="text"
              class="wa-filter-input"
              placeholder="Buscar..."
            />
          </div>

          <!-- Status -->
          <div class="wa-filter-section">
            <label class="wa-filter-label">Status do contato</label>
            <select v-model="filtroStatus" class="wa-filter-input">
              <option v-for="opt in statusOpcoes" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>

          <!-- Estados (UF) -->
          <div class="wa-filter-section">
            <label class="wa-filter-label">
              UF
              <span v-if="filtroEstados.length" class="wa-filter-count">{{ filtroEstados.length }}</span>
            </label>
            <div class="wa-filter-uf-grid">
              <button
                v-for="uf in ufs"
                :key="uf"
                type="button"
                class="wa-filter-chip"
                :class="{ ativo: filtroEstados.includes(uf) }"
                @click="toggleEstado(uf)"
              >{{ uf }}</button>
            </div>
          </div>

          <!-- Etiquetas (multiselect com busca) -->
          <div class="wa-filter-section wa-filter-tags-section">
            <label class="wa-filter-label">
              Etiquetas
              <span v-if="filtroRotulosIds.length" class="wa-filter-count">{{ filtroRotulosIds.length }}</span>
            </label>
            <div v-if="!etiquetasDisponiveis.length" class="wa-filter-vazio">
              Nenhuma etiqueta cadastrada.
            </div>
            <Multiselect
              v-else
              v-model="filtroRotulosIds"
              :options="etiquetasDisponiveis"
              mode="tags"
              label="label"
              value-prop="value"
              :searchable="true"
              :close-on-select="false"
              placeholder="Buscar etiqueta..."
              no-results-text="Nenhuma etiqueta encontrada"
              no-options-text="Sem etiquetas"
            >
              <template #tag="{ option, handleTagRemove }">
                <span class="multiselect-tag" :style="{ background: option.cor }">
                  {{ option.label }}
                  <span class="multiselect-tag-remove" @click.prevent="handleTagRemove(option, $event)">
                    <span class="multiselect-tag-remove-icon"></span>
                  </span>
                </span>
              </template>
              <template #option="{ option }">
                <span class="wa-filter-tag-swatch" :style="{ backgroundColor: option.cor }"></span>
                {{ option.label }}
              </template>
            </Multiselect>
            <br>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Filter chips ===== -->
    <div class="wa-filters">
      <button
        class="wa-chip"
        :class="{ active: filtroTab === 'todas' }"
        @click="filtroTab = 'todas'"
      >Todas</button>
      <button
        class="wa-chip"
        :class="{ active: filtroTab === 'nao_lidas' }"
        @click="filtroTab = 'nao_lidas'"
      >
        Não lidas
        <span v-if="contNaoLidas" class="wa-chip-count">{{ contNaoLidas }}</span>
      </button>
      <button
        class="wa-chip"
        :class="{ active: filtroTab === 'atribuidas' }"
        @click="filtroTab = 'atribuidas'"
      >Minhas</button>
    </div>

    <!-- ===== Loading ===== -->
    <div v-if="loading" class="wa-empty">
      <i class="ri-loader-4-line wa-spin"></i>
      <span>Carregando...</span>
    </div>

    <!-- ===== Empty state ===== -->
    <div v-else-if="!conversasOrdenadas.length" class="wa-empty">
      <i class="ri-chat-3-line wa-empty-icon"></i>
      <span v-if="buscaLocal">Nenhum resultado para "{{ buscaLocal }}"</span>
      <span v-else-if="filtroTab === 'nao_lidas'">Nenhuma mensagem n\u00E3o lida</span>
      <span v-else>Nenhuma conversa</span>
    </div>

    <!-- ===== Conversation list ===== -->
    <div v-else class="wa-list" role="list">
      <div
        v-for="c in conversasOrdenadas"
        :key="c.id"
        class="wa-item"
        :class="{ 'wa-item--active': c.id === conversaAtivaId }"
        role="listitem"
        @click="selecionar(c.id)"
      >
        <!-- Avatar -->
        <div class="wa-avatar" :style="{ backgroundColor: corAvatar(c) }">
          {{ iniciais(c) }}
        </div>

        <!-- Content -->
        <div class="wa-item-body">
          <!-- Row 1: nome + telefone + hor\u00E1rio -->
          <div class="wa-row1">
            <span class="wa-name">{{ tituloConversa(c) }}<span v-if="temNome(c) && telefoneContato(c)" class="wa-phone d-none d-md-inline">{{ telefoneContato(c) }}</span></span>
            <span
              class="wa-time"
              :class="{ 'wa-time--unread': c.nao_lidas_count > 0 }"
            >{{ horarioWhatsapp(c) }}</span>
          </div>
          <!-- Row 2: preview + badges -->
          <div class="wa-row2">
            <div class="wa-preview">
              <span v-if="direcaoMsg(c) === 'saida'" class="wa-checks">
                <i v-if="statusEnvio(c) === 'lida'" class="ri-check-double-line wa-check--read"></i>
                <i v-else-if="statusEnvio(c) === 'entregue'" class="ri-check-double-line"></i>
                <i v-else class="ri-check-line"></i>
              </span>
              <span class="wa-preview-text">{{ previaMsg(c) }}</span>
            </div>
            <div class="wa-meta">
              <span v-if="c.nao_lidas_count > 0" class="wa-unread">
                {{ c.nao_lidas_count }}
              </span>
            </div>
          </div>
          <!-- Row 3: marcações (etapa/consentimento/atendente) + etiquetas.
               No desktop quebra em linhas; no mobile vira trilho deslizante
               horizontal que mostra TODAS as marcações e etiquetas. -->
          <div class="wa-chips-rail">
            <span
              v-if="estagio(c)"
              class="wa-stage-badge"
              :style="{ backgroundColor: estagio(c).cor || '#8696a0' }"
              :title="`Etapa: ${estagio(c).nome}`"
            >
              <i class="ri-layout-grid-line"></i>
              {{ estagio(c).nome }}
            </span>
            <span class="wa-consent" :class="badgeConsentimento(c).classe" :title="badgeConsentimento(c).texto">
              <i :class="badgeConsentimento(c).icone"></i>
              {{ badgeConsentimento(c).texto }}
            </span>
            <span v-if="atendenteNome(c)" class="wa-atendente" :title="'Atendente: ' + atendenteNome(c)">
              <i class="ri-user-line"></i> {{ atendenteNome(c) }}
            </span>
            <span v-else class="wa-atendente wa-atendente--livre" title="Sem atendente">
              <i class="ri-user-add-line"></i> Livre
            </span>
            <span v-if="etiquetas(c).length" class="wa-rail-sep" aria-hidden="true"></span>
            <span
              v-for="e in etiquetas(c)"
              :key="e.id"
              class="wa-tag"
              :style="{ backgroundColor: e.cor }"
              :title="e.nome"
            >{{ e.nome }}</span>
          </div>
        </div>

        <!-- Menu por card (3-dot) — visível no hover -->
        <div class="wa-item-menu-wrap" @click.stop>
          <button
            type="button"
            class="wa-item-menu-btn"
            title="Mais ações"
            @click.stop="toggleCardMenu(c.id)"
          >
            <i class="ri-more-2-fill"></i>
          </button>
          <div v-if="menuConvAbertoId === c.id" class="wa-item-menu-drop">
            <button class="wa-dropdown-item" @click.stop="abrirFinalizar(c)">
              <i class="ri-checkbox-circle-line me-2 text-success"></i>Finalizar conversa
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal nova conversa -->
    <IniciarConversaModal
      v-if="modalNovaConversa"
      @close="modalNovaConversa = false"
    />

    <!-- Modal: finalizar conversa -->
    <div v-if="finalizarModalAberto" class="finalizar-modal-overlay" @click.self="cancelarFinalizar">
      <div class="finalizar-modal">
        <header class="finalizar-modal-header">
          <i class="ri-checkbox-circle-line text-success"></i>
          <h6 class="m-0">Finalizar conversa</h6>
        </header>
        <div class="finalizar-modal-body">
          <p class="text-muted small mb-2">
            A conversa será movida para "Encerrado". O filiado continua podendo
            ser contatado no futuro (não é opt-out).
          </p>
          <label class="form-label small mb-1">Mensagem final (opcional)</label>
          <textarea
            v-model="mensagemFinalTexto"
            class="form-control form-control-sm"
            rows="3"
            :placeholder="finalizarJanelaInfo && !finalizarJanelaInfo.aberta
              ? 'Janela 24h fechada — texto livre não permitido'
              : 'Ex.: Obrigado pelo contato! Estamos à disposição.'"
            :disabled="(finalizarJanelaInfo && !finalizarJanelaInfo.aberta) || finalizandoConversa"
            maxlength="1024"
          ></textarea>
          <div
            v-if="finalizarJanelaInfo && !finalizarJanelaInfo.aberta"
            class="alert alert-warning py-1 px-2 mb-0 mt-2 d-flex align-items-center small"
          >
            <i class="ri-information-line me-1"></i>
            <span>Janela 24h fechada — finalize sem mensagem ou envie um template antes.</span>
          </div>
          <small v-else class="text-muted d-block mt-1">
            Deixe em branco se quiser apenas mover a conversa pra "Encerrado" sem avisar o filiado.
          </small>
        </div>
        <footer class="finalizar-modal-footer">
          <button type="button" class="btn btn-link" :disabled="finalizandoConversa" @click="cancelarFinalizar">
            Cancelar
          </button>
          <button type="button" class="btn btn-success" :disabled="finalizandoConversa" @click="confirmarFinalizar">
            <i v-if="finalizandoConversa" class="ri-loader-4-line spin me-1"></i>
            <i v-else class="ri-checkbox-circle-line me-1"></i>
            {{ finalizandoConversa ? 'Finalizando...' : 'Finalizar' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===================================================================
   WhatsApp Web — Sidebar (left panel)
   Pixel-accurate reproduction of WhatsApp Web 2024/2025 layout.
   =================================================================== */

/* --- CSS variables (light) ---------------------------------------- */
.wa-sidebar {
  --wa-bg: #fff;
  --wa-header-bg: #f0f2f5;
  --wa-border: #e9edef;
  --wa-text: #111b21;
  --wa-text2: #667781;
  --wa-icon: #54656f;
  --wa-search-bg: #f0f2f5;
  --wa-hover: #f5f6f6;
  --wa-active: var(--vz-primary-bg-subtle, #FFF4F4);
  --wa-primary: var(--brand-primary, #971219);
  --wa-primary-dim: rgba(var(--vz-primary-rgb, 151, 18, 25), 0.15);
  --wa-chip-bg: #e9edef;
  --wa-chip-hover: #d1d7db;
  --wa-dropdown-bg: #fff;
  --wa-dropdown-hover: #f5f6f6;
  --wa-scrollbar: rgba(0,0,0,0.2);
  --wa-check-read: #53bdeb;

  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 440px;
  min-width: 320px;
  background: var(--vz-card-bg);
  border-right: 1px solid var(--vz-border-color);
  overflow: hidden;
  user-select: none;
}

/* --- Header ------------------------------------------------------- */
.wa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: var(--wa-header-bg);
  min-height: 59px;
}
/* Mobile: sem header da lista — a visão é sempre Lista e o "nova conversa"
   vira FAB flutuante. */
@media (max-width: 767.98px) {
  .wa-header { display: none; }
}
.wa-header-left { display: flex; align-items: center; }
.wa-header-left :deep(.atendimento-visao-toggle) { flex-shrink: 1; }
.wa-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #dfe5e7;
  color: #8696a0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  cursor: pointer;
}
.wa-header-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.wa-header-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--wa-icon);
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.wa-header-btn:hover { background: rgba(0,0,0,0.05); }

/* --- Dropdown menu ------------------------------------------------ */
.wa-menu-wrapper { position: relative; }
.wa-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 200px;
  background: var(--wa-dropdown-bg);
  border-radius: 3px;
  box-shadow: 0 2px 5px 0 rgba(11,20,26,0.26), 0 2px 10px 0 rgba(11,20,26,0.16);
  padding: 10px 0;
  z-index: 10;
}
.wa-dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  border: none;
  background: transparent;
  color: var(--wa-text);
  font-size: 14.5px;
  padding: 10px 24px;
  cursor: pointer;
  text-align: left;
}
.wa-dropdown-item:hover { background: var(--wa-dropdown-hover); }

/* --- Search area -------------------------------------------------- */
.wa-search-area {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  gap: 6px;
  background: var(--wa-bg);
  border-bottom: 1px solid var(--wa-border);
}
.wa-search-box {
  flex: 1;
  display: flex;
  align-items: center;
  background: var(--wa-search-bg);
  border-radius: 8px;
  padding: 0 8px 0 12px;
  height: 35px;
  gap: 10px;
  transition: background 0.2s;
}
.wa-search-box.focused { background: #fff; box-shadow: 0 0 0 1px var(--wa-border); }
.wa-search-icon {
  color: var(--wa-icon);
  font-size: 16px;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.2s;
}
.wa-search-back {
  border: none;
  background: none;
  color: var(--wa-primary);
  font-size: 18px;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
}
.wa-search-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 13px;
  color: var(--wa-text);
  height: 100%;
}
.wa-search-input::placeholder { color: var(--wa-text2); }
.wa-filter-btn {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--wa-icon);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.wa-filter-btn:hover { background: rgba(0,0,0,0.05); }
.wa-filter-btn--ativo { color: var(--wa-primary); }
.wa-filter-popover-wrap { position: relative; }
.wa-filter-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--wa-primary);
  border: 2px solid var(--wa-bg);
}

/* --- Painel de filtros avançados --------------------------------- */
.wa-filter-popover {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 320px;
  max-width: 360px;
  max-height: 70vh;
  overflow-y: auto;
  background: var(--wa-dropdown-bg);
  border: 1px solid var(--wa-border);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(11,20,26,0.18), 0 4px 12px rgba(11,20,26,0.10);
  padding: 4px 0 8px;
  z-index: 50;
}
.wa-filter-popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--wa-text);
  border-bottom: 1px solid var(--wa-border);
}
.wa-filter-clear {
  background: transparent;
  border: 0;
  color: var(--wa-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
}
.wa-filter-clear:hover { background: var(--wa-primary-dim); }

.wa-filter-section {
  padding: 10px 14px;
  border-bottom: 1px solid var(--wa-border);
}
.wa-filter-section:last-child { border-bottom: none; }
.wa-filter-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--wa-text2);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}
.wa-filter-count {
  background: var(--wa-primary);
  color: #fff;
  font-size: 10px;
  padding: 0 5px;
  border-radius: 8px;
  min-width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.wa-filter-input {
  width: 100%;
  border: 1px solid var(--wa-border);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  background: var(--wa-bg);
  color: var(--wa-text);
  outline: none;
  transition: border-color 0.15s;
}
.wa-filter-input:focus { border-color: var(--wa-primary); }
.wa-filter-uf-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.wa-filter-chip {
  border: 1px solid var(--wa-border);
  background: var(--wa-bg);
  color: var(--wa-text2);
  font-size: 11px;
  font-weight: 500;
  padding: 4px 0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.12s;
}
.wa-filter-chip:hover { background: var(--wa-hover); }
.wa-filter-chip.ativo {
  background: var(--wa-primary);
  color: #fff;
  border-color: var(--wa-primary);
}
.wa-filter-vazio {
  font-size: 12px;
  color: var(--wa-text2);
  font-style: italic;
}
.wa-filter-tag-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 8px;
  flex-shrink: 0;
}
/* Multiselect: limita altura quando lista de tags fica grande */
.wa-filter-tags-section :deep(.multiselect) {
  font-size: 12.5px;
  min-height: 36px;
}
.wa-filter-tags-section :deep(.multiselect-tags) { padding: 4px; gap: 3px; }
.wa-filter-tags-section :deep(.multiselect-tag) {
  font-size: 11.5px;
  padding: 2px 4px 2px 8px;
  border-radius: 12px;
  color: #fff;
}
.wa-filter-tags-section :deep(.multiselect-dropdown) {
  max-height: 220px;
  z-index: 60;
}
.wa-filter-tags-section :deep(.multiselect-option) {
  padding: 6px 10px;
  font-size: 13px;
  display: flex;
  align-items: center;
}

/* --- Filter chips ------------------------------------------------- */
.wa-filters {
  display: flex;
  gap: 8px;
  padding: 8px 16px 6px;
  overflow-x: auto;
}
.wa-filters::-webkit-scrollbar { display: none; }
.wa-chip {
  border: none;
  border-radius: 18px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  background: var(--wa-chip-bg);
  color: var(--wa-text2);
  transition: background 0.15s, color 0.15s;
  display: flex;
  align-items: center;
  gap: 4px;
}
.wa-chip:hover { background: var(--wa-chip-hover); }
.wa-chip.active {
  background: var(--wa-primary-dim);
  color: var(--wa-primary);
}
.wa-chip-count {
  background: var(--brand-primary, #971219);
  color: white;
  border-radius: 10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  vertical-align: middle;
  position: relative;
  top: -0.5px;
}

/* --- Empty / Loading ---------------------------------------------- */
.wa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: var(--wa-text2);
  font-size: 14px;
  padding: 40px 24px;
  text-align: center;
}
.wa-empty-icon { font-size: 52px; opacity: 0.35; }
.wa-spin { display: inline-block; animation: wa-spin 0.8s linear infinite; font-size: 24px; }
@keyframes wa-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* --- Conversation list -------------------------------------------- */
.wa-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}
.wa-list::-webkit-scrollbar { width: 6px; }
.wa-list::-webkit-scrollbar-thumb {
  background: var(--wa-scrollbar);
  border-radius: 3px;
}

/* --- Conversation item -------------------------------------------- */
.wa-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.1s;
  position: relative;
  border-bottom: 1px solid var(--vz-border-color);
}
.wa-item:last-child { border-bottom: none; }
.wa-item:hover { background: var(--vz-light); }
[data-bs-theme='dark'] .wa-item:hover { background: rgba(255, 255, 255, 0.04); }
.wa-item--active,
.wa-item--active:hover { background: var(--wa-active); }

/* Menu por card (3-dot) — sempre visível (padrão igual Grupos/Notícias) */
.wa-item-menu-wrap {
  position: absolute;
  top: 9px;
  right: 8px;
  z-index: 5;
}
.wa-item-menu-btn {
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--wa-icon, #54656f);
  font-size: 16px;
  cursor: pointer;
  opacity: 0.5;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s, background 0.12s;
}
.wa-item:hover .wa-item-menu-btn { opacity: 0.9; }
.wa-item-menu-btn:hover { background: rgba(0,0,0,0.08); opacity: 1; }
[data-bs-theme='dark'] .wa-item-menu-btn:hover { background: rgba(255,255,255,0.1); }
.wa-item-menu-drop {
  position: absolute;
  top: 28px;
  right: 0;
  min-width: 200px;
  background: var(--wa-dropdown-bg, #fff);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(11,20,26,0.3), 0 2px 6px rgba(11,20,26,0.15);
  padding: 6px 0;
  z-index: 10;
}
[data-bs-theme="dark"] .wa-item-menu-drop {
  background: #233138;
  box-shadow: 0 4px 16px rgba(0,0,0,0.5);
}

/* Modal de finalizar conversa */
.finalizar-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}
.finalizar-modal {
  background: var(--vz-body-bg);
  border-radius: 8px;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 12px 40px rgba(0,0,0,0.4);
  display: flex;
  flex-direction: column;
}
.finalizar-modal-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--vz-border-color);
}
.finalizar-modal-header i { font-size: 1.3rem; }
.finalizar-modal-body { padding: 1rem; }
.finalizar-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--vz-border-color);
}
@keyframes spin-rotate-inbox { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin-rotate-inbox 0.8s linear infinite; }

/* Avatar */
.wa-avatar {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  font-size: 16px;
  margin-top: 1px;
  letter-spacing: 0.5px;
}

/* Body */
.wa-item-body {
  flex: 1;
  min-width: 0;
}

/* Row 1: name + time (padding à direita reserva espaço p/ o 3-pontos fixo) */
.wa-row1 {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 3px;
  padding-right: 22px;
}
.wa-name {
  font-size: 0.825rem;
  font-weight: 600;
  color: var(--wa-text);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  margin-right: 6px;
}
.wa-phone {
  font-size: 11.5px;
  font-weight: 400;
  color: var(--wa-text2);
  margin-left: 6px;
}
.wa-time {
  font-size: 0.72rem;
  color: var(--wa-text2);
  white-space: nowrap;
  flex-shrink: 0;
}
.wa-time--unread { color: var(--wa-primary); }

/* Row 2: preview + badges */
.wa-row2 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
}
.wa-preview {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  gap: 3px;
}
.wa-checks {
  flex-shrink: 0;
  font-size: 16px;
  color: var(--wa-text2);
  line-height: 1;
}
.wa-check--read { color: var(--wa-check-read); }
.wa-preview-text {
  font-size: 0.72rem;
  color: var(--wa-text2);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.4;
}

/* Badges area */
.wa-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.wa-unread {
  background: var(--wa-primary);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  line-height: 1;
}

/* --- Trilho de marcações (etapa/consentimento/atendente) + etiquetas ---
   Desktop: quebra em linhas (mostra tudo empilhado).
   Mobile: rola horizontalmente (swipe) mostrando TODAS as marcações e
   etiquetas, com um leve esmaecido à direita indicando que há mais. */
.wa-chips-rail {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.wa-rail-sep {
  display: none; /* só no trilho de linha única (mobile) */
  width: 1px;
  min-height: 15px;
  background: var(--wa-border);
  margin: 0 3px;
  flex-shrink: 0;
}
@media (max-width: 767.98px) {
  .wa-chips-rail {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 20px), transparent);
    mask-image: linear-gradient(to right, #000 calc(100% - 20px), transparent);
  }
  .wa-chips-rail::-webkit-scrollbar { display: none; }
  .wa-chips-rail > * { flex-shrink: 0; }
  .wa-rail-sep { display: block; }
  /* respiro no fim pra última etiqueta não encostar no fade */
  .wa-chips-rail::after { content: ''; flex: 0 0 6px; }
}
.wa-stage-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 16px;
  max-width: 130px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: inset 0 -1px 0 rgba(0,0,0,0.12);
}
.wa-stage-badge i {
  font-size: 10px;
  flex-shrink: 0;
}
.wa-consent {
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  line-height: 16px;
}
.wa-consent i { font-size: 10px; }
.wa-consent--optin   { background: #d1fae5; color: #065f46; }
.wa-consent--optout  { background: #fee2e2; color: #991b1b; }
.wa-consent--pending { background: #fef3c7; color: #92400e; }
.wa-consent--expired { background: #e5e7eb; color: #6b7280; }

.wa-atendente {
  font-size: 10px;
  font-weight: 500;
  padding: 1px 6px;
  border-radius: 3px;
  background: #e0f2fe;
  color: #0369a1;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  line-height: 16px;
}
.wa-atendente i { font-size: 10px; }
.wa-atendente--livre {
  background: #fef3c7;
  color: #92400e;
}

.wa-tag {
  font-size: 10px;
  font-weight: 500;
  padding: 1px 8px;
  border-radius: 10px;
  color: #fff;
  max-width: 96px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 16px;
  box-shadow: inset 0 -1px 0 rgba(0,0,0,0.12);
}

/* ===================================================================
   Dark mode — data-bs-theme="dark" (Velzon) override
   =================================================================== */
[data-bs-theme='dark'] .wa-sidebar {
  --wa-bg: #111b21;
  --wa-header-bg: #202c33;
  --wa-border: #2a3942;
  --wa-text: #e9edef;
  --wa-text2: #8696a0;
  --wa-icon: #aebac1;
  --wa-search-bg: #202c33;
  --wa-hover: #202c33;
  --wa-active: var(--vz-primary-bg-subtle, #2a3942);
  --wa-primary: var(--brand-primary, #971219);
  --wa-primary-dim: rgba(var(--vz-primary-rgb, 151, 18, 25), 0.18);
  --wa-chip-bg: #202c33;
  --wa-chip-hover: #374045;
  --wa-dropdown-bg: #233138;
  --wa-dropdown-hover: #182229;
  --wa-scrollbar: rgba(255,255,255,0.15);
  --wa-check-read: #53bdeb;
}
[data-bs-theme='dark'] .wa-search-box.focused {
  background: #2a3942;
  box-shadow: none;
}
[data-bs-theme='dark'] .wa-user-avatar {
  background: #374045;
  color: #aebac1;
}
[data-bs-theme='dark'] .wa-consent--optin   { background: #064e3b; color: #6ee7b7; }
[data-bs-theme='dark'] .wa-consent--optout  { background: #7f1d1d; color: #fca5a5; }
[data-bs-theme='dark'] .wa-consent--pending { background: #78350f; color: #fcd34d; }
[data-bs-theme='dark'] .wa-consent--expired { background: #374151; color: #9ca3af; }
[data-bs-theme='dark'] .wa-atendente        { background: #0c4a6e; color: #7dd3fc; }
[data-bs-theme='dark'] .wa-atendente--livre { background: #78350f; color: #fcd34d; }

@media (max-width: 360px) {
  .wa-header-left :deep(.atendimento-visao-toggle .btn) {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}
</style>
